/**
 * SYS-16 «Функции ограничены».
 *
 * ПС §14, BS §6.3: при активной санкции пользователь видит, ЧТО именно
 * ограничено и до какого срока, и может перейти к S-25 — а не сталкивается
 * с молча неработающими кнопками.
 *
 * Показывается баннером над содержимым, не перекрывая интерфейс: санкция —
 * не блокирующее состояние (SYS-08 отдельно, для `suspended`).
 */
import { useNavigate } from '@tanstack/react-router'

import { Icon } from '@shared/ui'

import { useAppStore, selectActiveSanction } from '../../model/app-store'
import { formatDateTime } from '../../lib/format'
import styles from './restriction.module.css'

const SANCTION_TEXT: Record<string, { title: string; text: string }> = {
  warning: {
    title: 'Предупреждение по аккаунту',
    text: 'Функции работают как обычно, но повторное нарушение приведёт к ограничению.',
  },
  read_only: {
    title: 'Отправка сообщений ограничена',
    text: 'Читать переписку и пользоваться приложением можно; отправлять сообщения, нажимать «Го» и загружать медиа — пока нет.',
  },
  hidden: {
    title: 'Профиль скрыт из рекомендаций',
    text: 'Вас не показывают новым людям. Существующие диалоги работают как обычно.',
  },
  banned: {
    title: 'Доступ к аккаунту заблокирован',
    text: 'Действия с профилем и перепиской недоступны.',
  },
}

export function RestrictionBanner(): React.ReactElement | null {
  const navigate = useNavigate()
  const sanction = useAppStore(selectActiveSanction)

  if (!sanction) return null

  const copy = SANCTION_TEXT[sanction.type] ?? {
    title: 'Аккаунт ограничен',
    text: 'Часть функций временно недоступна.',
  }

  /*
   * Компактная полоса, а не карточка с абзацем: баннер стоит над каждым
   * экраном, и на телефоне занимал бы треть экрана поверх диалога. Вся
   * полоса — кнопка в S-25, где есть полное объяснение и апелляция.
   */
  return (
    <button
      type="button"
      className={styles['strip']}
      onClick={() => void navigate({ to: '/settings/$section', params: { section: 'restrictions' } })}
    >
      <span className={styles['icon']}>
        <Icon name="shield" size={18} />
      </span>
      <span className={styles['body']}>
        <span className={styles['title']}>{copy.title}</span>
        <span className={styles['meta']}>
          {sanction.ends_at ? `До ${formatDateTime(sanction.ends_at)} · ` : ''}
          <span className={styles['text']}>{copy.text}</span>
          <span className={styles['more']}>Подробнее</span>
        </span>
      </span>
      <span className={styles['chevron']}>
        <Icon name="chevron-right" size={18} />
      </span>
    </button>
  )
}
